import { useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import styles from './FilterBar.module.css';

type SortOrder = 'DESC' | 'ASC';

interface FilterBarProps {
  sortOrder: SortOrder;
  onSortOrderChange: (order: SortOrder) => void;
  specialty: string;
  onSpecialtyChange: (specialty: string) => void;
  procedure: string;
  onProcedureChange: (procedure: string) => void;
  specialtyOptions?: string[];
  procedureOptions?: string[];
  extraFilters?: ReactNode;
}

type DropdownType = 'sortOrder' | 'specialty' | 'procedure' | null;

const SORT_OPTIONS: { value: SortOrder; label: string }[] = [
  { value: 'DESC', label: '新しい順' },
  { value: 'ASC', label: '古い順' },
];

const FilterBar = ({
  sortOrder,
  onSortOrderChange,
  specialty,
  onSpecialtyChange,
  procedure,
  onProcedureChange,
  specialtyOptions = [],
  procedureOptions = [],
  extraFilters,
}: FilterBarProps) => {
  const [openDropdown, setOpenDropdown] = useState<DropdownType>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = useCallback((type: DropdownType) => {
    setOpenDropdown((prev) => (prev === type ? null : type));
  }, []);

  const handleClear = useCallback(() => {
    onSortOrderChange('DESC');
    onSpecialtyChange('');
    onProcedureChange('');
    setOpenDropdown(null);
  }, [onSortOrderChange, onSpecialtyChange, onProcedureChange]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sortLabel = SORT_OPTIONS.find((o) => o.value === sortOrder)?.label ?? '投稿日';

  return (
    <div className={styles['FilterBar']} ref={wrapperRef}>
      <span className={styles['FilterBar-label']}>絞り込み</span>

      <div className={styles['FilterBar-filters']}>
        {extraFilters}
        {/* 投稿日 */}
        <div className={styles['FilterBar-dropdownWrapper']}>
          <button
            className={`${styles['FilterBar-button']} ${openDropdown === 'sortOrder' ? styles['FilterBar-button--active'] : ''}`}
            onClick={() => toggleDropdown('sortOrder')}
            type="button"
          >
            {sortLabel}
            <span className={styles['FilterBar-buttonArrow']} />
          </button>
          {openDropdown === 'sortOrder' && (
            <div className={styles['FilterBar-dropdown']}>
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`${styles['FilterBar-dropdownItem']} ${sortOrder === opt.value ? styles['FilterBar-dropdownItem--selected'] : ''}`}
                  onClick={() => {
                    onSortOrderChange(opt.value);
                    setOpenDropdown(null);
                  }}
                  type="button"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 診療科。候補が無いときは押せない。絞り込み中は色を変えて分かるようにする */}
        <div className={styles['FilterBar-dropdownWrapper']}>
          <button
            className={[
              styles['FilterBar-button'],
              openDropdown === 'specialty' ? styles['FilterBar-button--active'] : '',
              specialty ? styles['FilterBar-button--selected'] : '',
            ].join(' ')}
            onClick={() => toggleDropdown('specialty')}
            type="button"
            disabled={specialtyOptions.length === 0}
            aria-pressed={Boolean(specialty)}
            title={specialty ? `診療科「${specialty}」で絞り込み中` : undefined}
            data-testid="filter-bar__specialty-button"
          >
            {specialty || '診療科'}
            <span className={styles['FilterBar-buttonArrow']} />
          </button>
          {openDropdown === 'specialty' && specialtyOptions.length > 0 && (
            <div className={styles['FilterBar-dropdown']}>
              {specialtyOptions.map((opt) => (
                <button
                  key={opt}
                  className={`${styles['FilterBar-dropdownItem']} ${specialty === opt ? styles['FilterBar-dropdownItem--selected'] : ''}`}
                  onClick={() => {
                    onSpecialtyChange(opt);
                    setOpenDropdown(null);
                  }}
                  type="button"
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 術式。候補が渡されるまでは出さない（押しても何も起きないボタンを置かない） */}
        {procedureOptions.length > 0 && (
        <div className={styles['FilterBar-dropdownWrapper']}>
          <button
            className={`${styles['FilterBar-button']} ${openDropdown === 'procedure' ? styles['FilterBar-button--active'] : ''}`}
            onClick={() => toggleDropdown('procedure')}
            type="button"
          >
            {procedure || '術式'}
            <span className={styles['FilterBar-buttonArrow']} />
          </button>
          {openDropdown === 'procedure' && (
            <div className={styles['FilterBar-dropdown']}>
              {procedureOptions.map((opt) => (
                <button
                  key={opt}
                  className={`${styles['FilterBar-dropdownItem']} ${procedure === opt ? styles['FilterBar-dropdownItem--selected'] : ''}`}
                  onClick={() => {
                    onProcedureChange(opt);
                    setOpenDropdown(null);
                  }}
                  type="button"
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>
        )}
      </div>

      <button
        className={styles['FilterBar-clear']}
        onClick={handleClear}
        type="button"
      >
        内容をクリア
      </button>
    </div>
  );
};

export default FilterBar;
